/* LogoReviewTab — PROP-002 / iter 85.12.

   Embedded inside LogoEnrichmentRunDetail.jsx as the new "Review" tab.
   Scoped to the parent run's run_id. Per operator amendment #3 there is
   NO standalone Logo Review Queue page for v1; the review workflow
   lives attached to Enrichment Runs.
*/

import { useCallback, useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import LogoReviewCard from "./LogoReviewCard";

export default function LogoReviewTab({ runId }) {
    const [state, setState] = useState("pending_review");
    const [minConfidence, setMinConfidence] = useState(0.65);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [data, setData] = useState({ items: [], total: 0, state_counts: {} });
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchPage = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = {
                run_id: runId,
                state,
                min_confidence: minConfidence,
                page,
                page_size: pageSize,
                sort: "confidence_score_desc",
            };
            if (search.trim()) params.q = search.trim();
            const [{ data: json }, { data: statsJson }] = await Promise.all([
                api.get("/data/admin/logos/review-queue", { params }),
                api.get("/data/admin/logos/review-queue-stats", { params: { run_id: runId } }),
            ]);
            setData(json);
            setStats(statsJson);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [runId, state, minConfidence, search, page, pageSize]);

    useEffect(() => {
        if (runId) fetchPage();
    }, [fetchPage, runId]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil((data.total || 0) / pageSize)),
        [data.total, pageSize],
    );

    const counts = data.state_counts || {};

    return (
        <div className="space-y-4" data-testid="logo-review-tab">
            {/* Stats bar */}
            <div className="rounded-md border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-4 text-sm" data-testid="logo-review-tab-stats">
                <StatTile label="Pending" value={counts.pending_review || 0} tone="amber" testid="stat-pending" />
                <StatTile label="Approved" value={counts.approved || 0} tone="emerald" testid="stat-approved" />
                <StatTile label="Human Corrected" value={counts.human_corrected || 0} tone="cyan" testid="stat-human-corrected" />
                <StatTile label="Rejected" value={counts.rejected || 0} tone="rose" testid="stat-rejected" />
                <StatTile label="Skipped" value={counts.skipped || 0} tone="slate" testid="stat-skipped" />
                <StatTile label="Not Eligible" value={counts.not_eligible || 0} tone="slate" testid="stat-not-eligible" />
                {stats && stats.ai_accuracy_pct !== null && stats.ai_accuracy_pct !== undefined && (
                    <div
                        className="inline-flex items-baseline gap-1.5 px-2 py-1 rounded border border-indigo-200 bg-indigo-50"
                        title="approved / (approved + human_corrected)"
                        data-testid="stat-ai-accuracy"
                    >
                        <span className="font-mono font-semibold tabular-nums text-indigo-700">
                            {stats.ai_accuracy_pct.toFixed(1)}%
                        </span>
                        <span className="text-xs text-indigo-700 uppercase tracking-wide">
                            AI Accuracy
                        </span>
                    </div>
                )}
                <div className="ml-auto text-xs text-slate-500">
                    <strong className="text-slate-700">AI proposes. Human verifies.</strong> Repository updates only after approval.
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3" data-testid="logo-review-tab-filters">
                <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    State:
                    <select
                        value={state}
                        onChange={(e) => { setPage(1); setState(e.target.value); }}
                        className="text-sm border border-slate-300 rounded px-2 py-1"
                        data-testid="logo-review-tab-state-filter"
                    >
                        <option value="pending_review">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="human_corrected">Human Corrected</option>
                        <option value="rejected">Rejected</option>
                        <option value="skipped">Skipped</option>
                        <option value="not_eligible">Not Eligible</option>
                        <option value="all">All</option>
                    </select>
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    Min confidence:
                    <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={minConfidence}
                        onChange={(e) => { setPage(1); setMinConfidence(parseFloat(e.target.value) || 0); }}
                        className="w-20 text-sm border border-slate-300 rounded px-2 py-1 font-mono"
                        data-testid="logo-review-tab-min-confidence"
                    />
                </label>
                <input
                    type="search"
                    placeholder="Search company / domain / symbol"
                    value={search}
                    onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                    className="text-sm border border-slate-300 rounded px-2 py-1 flex-1 min-w-[200px] max-w-[360px]"
                    data-testid="logo-review-tab-search"
                />
                <button
                    type="button"
                    onClick={fetchPage}
                    disabled={loading}
                    className="text-xs text-slate-500 hover:text-slate-900 underline"
                    data-testid="logo-review-tab-refresh"
                >
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="logo-review-tab-error">
                    {error}
                </div>
            )}

            {/* Cards */}
            <div className="space-y-3" data-testid="logo-review-tab-cards">
                {!loading && data.items.length === 0 && (
                    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500" data-testid="logo-review-tab-empty">
                        {state === "pending_review"
                            ? "No pending reviews for this run. All eligible candidates have been processed."
                            : `No outcomes in state "${state}" for this run.`}
                    </div>
                )}
                {data.items.map((card) => (
                    <LogoReviewCard
                        key={card.outcome_id}
                        card={card}
                        onUpdated={fetchPage}
                    />
                ))}
            </div>

            {/* Pagination */}
            {data.total > pageSize && (
                <div className="flex items-center justify-between pt-2" data-testid="logo-review-tab-pagination">
                    <div className="text-xs text-slate-500">
                        Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, data.total)} of {data.total}
                    </div>
                    <div className="inline-flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1 || loading}
                            className="px-2 py-1 rounded border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="logo-review-tab-prev"
                        >
                            <CaretLeft size={12} /> Prev
                        </button>
                        <span className="text-xs text-slate-600">
                            Page {page} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || loading}
                            className="px-2 py-1 rounded border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="logo-review-tab-next"
                        >
                            Next <CaretRight size={12} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, tone, testid }) {
    const toneMap = {
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        cyan: "text-cyan-700",
        rose: "text-rose-700",
        slate: "text-slate-600",
    };
    return (
        <div className="inline-flex items-baseline gap-1.5" data-testid={testid}>
            <span className={`font-mono font-semibold tabular-nums ${toneMap[tone] || ""}`}>{value}</span>
            <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
        </div>
    );
}
